import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import Dashboard from '../components/Dashboard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  FolderOpen,
  Mail,
  LogOut,
  Menu,
  X,
  Settings,
  Send,
  Check,
  AlertCircle,
  Star,
  Upload,
  Image,
  Home,
  Key,
  Users,
  Eye,
  EyeOff,
  ExternalLink,
  BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const { user, token, logout, isAdmin, loading: authLoading } = useAuth();
  
  const [artworks, setArtworks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [deletingArtwork, setDeletingArtwork] = useState(null);
  
  // Email settings states
  const [emailSettings, setEmailSettings] = useState({
    forwarding_enabled: false,
    forwarding_email: '',
    gmail_address: '',
    gmail_app_password: '',
    gmail_configured: false
  });
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Site settings states
  const [siteSettings, setSiteSettings] = useState({
    hero_image_url: ''
  });
  const [siteSaving, setSiteSaving] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const heroFileInputRef = useRef(null);
  
  // Artwork image upload states
  const [artworkImages, setArtworkImages] = useState([]);
  const [artworkUploading, setArtworkUploading] = useState(false);
  const artworkFileInputRef = useRef(null);
  
  // Password management states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    dimensions: '',
    description: '',
    image_url: '',
    images: [],
    year: '',
    category: 'Public Art',
    status: 'available'
  });

  // Redirect if not admin - only run once auth is loaded
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/login');
    }
  }, [authLoading, isAdmin, navigate]);

  // Fetch data - separate useEffect
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      if (!token || !isAdmin) {
        setLoading(false);
        return;
      }
      
      try {
        // Fetch artworks (public endpoint)
        const artworksRes = await axios.get(`${API}/artworks`);
        if (isMounted) {
          setArtworks(artworksRes.data);
        }
        
        // Fetch messages (admin only)
        try {
          const messagesRes = await axios.get(`${API}/contact`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (isMounted) {
            setMessages(messagesRes.data);
          }
        } catch (msgError) {
          console.error('Error fetching messages:', msgError);
        }
        
        // Fetch email settings (admin only)
        try {
          const emailRes = await axios.get(`${API}/settings/email`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (isMounted) {
            setEmailSettings(prev => ({
              ...prev,
              ...emailRes.data,
              gmail_app_password: '' // Never show password
            }));
          }
        } catch (emailError) {
          console.error('Error fetching email settings:', emailError);
        }
        
        // Fetch site settings (public endpoint)
        try {
          const siteRes = await axios.get(`${API}/settings/site`);
          if (isMounted) {
            setSiteSettings({
              hero_image_url: siteRes.data.hero_image_url || ''
            });
          }
        } catch (siteError) {
          console.error('Error fetching site settings:', siteError);
        }
        
        // Fetch all users (for password management)
        try {
          const usersRes = await axios.get(`${API}/auth/users`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (isMounted) {
            setAllUsers(usersRes.data);
          }
        } catch (usersError) {
          console.error('Error fetching users:', usersError);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!authLoading) {
      fetchData();
    }
    
    return () => {
      isMounted = false;
    };
  }, [token, isAdmin, authLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordChanging(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleResetUserPassword = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !resetPassword) {
      toast.error('Please select a user and enter a new password');
      return;
    }
    
    if (resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.post(`${API}/auth/admin-reset-password`, {
        user_email: selectedUser,
        new_password: resetPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Password reset for ${selectedUser}`);
      setSelectedUser('');
      setResetPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setResettingPassword(false);
    }
  };

  // File upload handlers
  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setHeroUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Use relative URL so it works on any domain
      const imageUrl = response.data.url;
      setSiteSettings(prev => ({ ...prev, hero_image_url: imageUrl }));
      toast.success('Image uploaded! Click Save to apply changes.');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setHeroUploading(false);
    }
  };

  const saveSiteSettings = async () => {
    setSiteSaving(true);
    try {
      await axios.put(`${API}/settings/site`, {
        hero_image_url: siteSettings.hero_image_url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Site settings saved!');
    } catch (error) {
      console.error('Error saving site settings:', error);
      toast.error('Failed to save site settings');
    } finally {
      setSiteSaving(false);
    }
  };

  const handleArtworkImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setArtworkUploading(true);
    const formDataUpload = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }
    
    try {
      const response = await axios.post(`${API}/upload/multiple`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Use relative URLs so they work on any domain
      const newImages = response.data.uploaded.map(item => item.url);
      
      // Check 10 image limit
      const currentCount = artworkImages.length;
      const availableSlots = 10 - currentCount;
      
      if (availableSlots <= 0) {
        toast.error('Maximum 10 images allowed per artwork');
        return;
      }
      
      const imagesToAdd = newImages.slice(0, availableSlots);
      if (imagesToAdd.length < newImages.length) {
        toast.warning(`Only added ${imagesToAdd.length} images (max 10 per artwork)`);
      }
      
      setArtworkImages(prev => [...prev, ...imagesToAdd]);
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...imagesToAdd],
        image_url: prev.image_url || imagesToAdd[0] || ''
      }));
      
      toast.success(`${imagesToAdd.length} image(s) uploaded!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setArtworkUploading(false);
      if (artworkFileInputRef.current) {
        artworkFileInputRef.current.value = '';
      }
    }
  };

  const removeArtworkImage = (indexToRemove) => {
    setArtworkImages(prev => prev.filter((_, i) => i !== indexToRemove));
    setFormData(prev => {
      const newImages = (prev.images || []).filter((_, i) => i !== indexToRemove);
      return {
        ...prev,
        images: newImages,
        image_url: newImages[0] || ''
      };
    });
  };

  const setPrimaryImage = (index) => {
    if (index === 0) return; // Already primary
    
    setArtworkImages(prev => {
      const newImages = [...prev];
      const [selected] = newImages.splice(index, 1);
      newImages.unshift(selected);
      return newImages;
    });
    
    setFormData(prev => {
      const newImages = [...(prev.images || [])];
      const [selected] = newImages.splice(index, 1);
      newImages.unshift(selected);
      return {
        ...prev,
        images: newImages,
        image_url: selected
      };
    });
    
    toast.success('Primary image updated');
  };

  const openAddModal = () => {
    setEditingArtwork(null);
    setArtworkImages([]);
    setFormData({
      title: '',
      dimensions: '',
      description: '',
      image_url: '',
      images: [],
      year: '',
      category: 'Public Art',
      status: 'available'
    });
    setIsModalOpen(true);
  };

  const openEditModal = (artwork) => {
    setEditingArtwork(artwork);
    const images = artwork.images?.length > 0 ? artwork.images : (artwork.image_url ? [artwork.image_url] : []);
    setArtworkImages(images);
    setFormData({
      title: artwork.title,
      dimensions: artwork.dimensions,
      description: artwork.description || '',
      image_url: artwork.image_url || images[0] || '',
      images: images,
      year: artwork.year || '',
      category: artwork.category || 'Public Art',
      status: artwork.status || 'available'
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const submitData = {
        ...formData,
        images: artworkImages,
        image_url: artworkImages[0] || formData.image_url
      };
      
      if (editingArtwork) {
        await axios.put(`${API}/artworks/${editingArtwork.id}`, submitData, { headers });
        toast.success('Artwork updated successfully!');
      } else {
        await axios.post(`${API}/artworks`, submitData, { headers });
        toast.success('Artwork added successfully!');
      }
      
      // Refresh artworks
      const response = await axios.get(`${API}/artworks`);
      setArtworks(response.data);
      setIsModalOpen(false);
      setArtworkImages([]);
    } catch (error) {
      console.error('Error saving artwork:', error);
      toast.error('Failed to save artwork');
    }
  };

  const confirmDelete = (artwork) => {
    setDeletingArtwork(artwork);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingArtwork) return;
    
    try {
      await axios.delete(`${API}/artworks/${deletingArtwork.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Artwork deleted successfully!');
      setArtworks(prev => prev.filter(a => a.id !== deletingArtwork.id));
      setIsDeleteModalOpen(false);
      setDeletingArtwork(null);
    } catch (error) {
      console.error('Error deleting artwork:', error);
      toast.error('Failed to delete artwork');
    }
  };

  const toggleFeatured = async (artworkId) => {
    try {
      const response = await axios.put(`${API}/artworks/${artworkId}/featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setArtworks(prev => prev.map(a => 
        a.id === artworkId ? { ...a, featured: response.data.featured } : a
      ));
      
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error toggling featured:', error);
      const message = error.response?.data?.detail || 'Failed to update featured status';
      toast.error(message);
    }
  };

  const markMessageRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Email settings handlers
  const handleEmailSettingsChange = (field, value) => {
    setEmailSettings(prev => ({ ...prev, [field]: value }));
  };

  const saveEmailSettings = async () => {
    setEmailSaving(true);
    try {
      const payload = {
        forwarding_enabled: emailSettings.forwarding_enabled,
        forwarding_email: emailSettings.forwarding_email || null,
        gmail_address: emailSettings.gmail_address || null,
        gmail_app_password: emailSettings.gmail_app_password || null
      };
      
      const response = await axios.put(`${API}/settings/email`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmailSettings(prev => ({
        ...prev,
        ...response.data,
        gmail_app_password: '' // Clear password field after save
      }));
      
      setIsEditingEmail(false);
      toast.success('Email settings saved successfully!');
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error('Failed to save email settings');
    } finally {
      setEmailSaving(false);
    }
  };

  const testEmailSettings = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API}/settings/email/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test email sent to ${response.data.sent_to}`);
    } catch (error) {
      console.error('Error testing email:', error);
      const message = error.response?.data?.detail || 'Failed to send test email';
      toast.error(message);
    } finally {
      setTestingEmail(false);
    }
  };

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950">
        <div className="spinner" data-testid="loading-spinner" />
      </div>
    );
  }

  // Don't render admin content if not admin
  if (!isAdmin) {
    return null;
  }

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="min-h-screen bg-neutral-950" data-testid="admin-page">
      {/* Mobile sidebar toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-neutral-800 shadow-md rounded-sm text-white"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="sidebar-toggle"
      >
        {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-neutral-900 border-r border-white/10 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-white/10">
          <h1 className="font-heading text-xl font-semibold text-white">
            Admin Panel
          </h1>
          <p className="text-white/50 text-sm mt-1">{user?.name}</p>
        </div>

        <nav className="p-4 space-y-2">
          <button
            onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'dashboard' 
                ? 'bg-teal-500/10 text-teal-400' 
                : 'text-white/60 hover:bg-white/5'
            }`}
            data-testid="nav-dashboard"
          >
            <BarChart3 className="h-5 w-5" />
            <span className="font-medium">Dashboard</span>
          </button>

          <button
            onClick={() => { setActiveTab('artworks'); setSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'artworks' 
                ? 'bg-teal-500/10 text-teal-400' 
                : 'text-white/60 hover:bg-white/5'
            }`}
            data-testid="nav-artworks"
          >
            <FolderOpen className="h-5 w-5" />
            <span className="font-medium">Artworks</span>
          </button>

          <button
            onClick={() => { setActiveTab('messages'); setSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'messages' 
                ? 'bg-teal-500/10 text-teal-400' 
                : 'text-white/60 hover:bg-white/5'
            }`}
            data-testid="nav-messages"
          >
            <Mail className="h-5 w-5" />
            <span className="font-medium">Messages</span>
            {unreadCount > 0 && (
              <span className="ml-auto bg-teal-500 text-black text-xs px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={() => { setActiveTab('site-settings'); setSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'site-settings' 
                ? 'bg-teal-500/10 text-teal-400' 
                : 'text-white/60 hover:bg-white/5'
            }`}
            data-testid="nav-site-settings"
          >
            <Home className="h-5 w-5" />
            <span className="font-medium">Site Settings</span>
          </button>

          <button
            onClick={() => { setActiveTab('account'); setSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'account' 
                ? 'bg-teal-500/10 text-teal-400' 
                : 'text-white/60 hover:bg-white/5'
            }`}
            data-testid="nav-account"
          >
            <Key className="h-5 w-5" />
            <span className="font-medium">Account & Security</span>
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-2">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center space-x-3 px-4 py-3 text-teal-400 hover:bg-teal-500/10 rounded-sm transition-colors"
            data-testid="return-to-website-btn"
          >
            <ExternalLink className="h-5 w-5" />
            <span className="font-medium">Return to Website</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-sm transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && <Dashboard token={token} />}

          {/* Artworks Tab */}
          {activeTab === 'artworks' && (
            <div data-testid="artworks-section">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-white">
                    Artworks
                  </h2>
                  <p className="text-white/50 text-sm mt-1">
                    Manage your portfolio pieces
                  </p>
                </div>
                <Button 
                  onClick={openAddModal} 
                  className="btn-primary"
                  data-testid="add-artwork-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Artwork
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : (
                <div className="bg-neutral-900 border border-white/10 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="w-10 text-white/60 text-center">
                          <Star className="h-4 w-4 mx-auto" />
                        </TableHead>
                        <TableHead className="w-16 text-white/60">Image</TableHead>
                        <TableHead className="text-white/60">Title</TableHead>
                        <TableHead className="hidden md:table-cell text-white/60">Dimensions</TableHead>
                        <TableHead className="hidden lg:table-cell text-white/60">Year</TableHead>
                        <TableHead className="hidden sm:table-cell text-white/60">Status</TableHead>
                        <TableHead className="w-24 text-white/60">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {artworks.length === 0 ? (
                        <TableRow className="border-white/10">
                          <TableCell colSpan={7} className="text-center py-12 text-white/50">
                            No artworks found. Add your first piece!
                          </TableCell>
                        </TableRow>
                      ) : (
                        artworks.map((artwork) => (
                          <TableRow key={artwork.id} className="border-white/10 hover:bg-white/5" data-testid={`artwork-row-${artwork.id}`}>
                            <TableCell className="text-center">
                              <button
                                onClick={() => toggleFeatured(artwork.id)}
                                className={`p-1.5 rounded-sm transition-all ${
                                  artwork.featured 
                                    ? 'text-yellow-400 hover:text-yellow-300' 
                                    : 'text-white/20 hover:text-white/40'
                                }`}
                                title={artwork.featured ? 'Remove from featured' : 'Add to featured (max 3)'}
                                data-testid={`featured-btn-${artwork.id}`}
                              >
                                <Star className={`h-5 w-5 ${artwork.featured ? 'fill-current' : ''}`} />
                              </button>
                            </TableCell>
                            <TableCell>
                              <div className="w-12 h-12 bg-neutral-800 overflow-hidden">
                                <img
                                  src={artwork.image_url}
                                  alt={artwork.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-white">{artwork.title}</TableCell>
                            <TableCell className="hidden md:table-cell text-white/50">
                              {artwork.dimensions}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-white/50">
                              {artwork.year || '-'}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <span className={`text-xs px-2 py-1 rounded-sm uppercase tracking-wider ${
                                artwork.status === 'sold' 
                                  ? 'bg-white/10 text-white/60' 
                                  : 'bg-teal-500/10 text-teal-400'
                              }`}>
                                {artwork.status === 'sold' ? 'Sold' : 'Available'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => openEditModal(artwork)}
                                  className="p-2 text-white/40 hover:text-teal-400 transition-colors"
                                  data-testid={`edit-btn-${artwork.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => confirmDelete(artwork)}
                                  className="p-2 text-white/40 hover:text-red-400 transition-colors"
                                  data-testid={`delete-btn-${artwork.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div data-testid="messages-section">
              <div className="mb-8">
                <h2 className="font-heading text-2xl font-semibold text-white">
                  Messages
                </h2>
                <p className="text-white/50 text-sm mt-1">
                  Contact form submissions
                </p>
              </div>

              {/* Email Forwarding Settings */}
              <div className="bg-neutral-900 border border-white/10 rounded-sm p-6 mb-8" data-testid="email-settings">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Settings className="h-5 w-5 text-white/40" />
                    <h3 className="font-medium text-white">Email Forwarding</h3>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-white/50">
                      {emailSettings.forwarding_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={emailSettings.forwarding_enabled}
                      onCheckedChange={(checked) => {
                        handleEmailSettingsChange('forwarding_enabled', checked);
                        if (!isEditingEmail) setIsEditingEmail(true);
                      }}
                      data-testid="forwarding-toggle"
                    />
                  </div>
                </div>

                {emailSettings.forwarding_enabled && (
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    <p className="text-sm text-white/50">
                      Forward new contact messages to your email inbox.
                    </p>

                    {/* Forwarding Email */}
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                        Forward messages to
                      </Label>
                      <Input
                        type="email"
                        value={emailSettings.forwarding_email || ''}
                        onChange={(e) => {
                          handleEmailSettingsChange('forwarding_email', e.target.value);
                          if (!isEditingEmail) setIsEditingEmail(true);
                        }}
                        placeholder="your@email.com"
                        className="max-w-md form-input"
                        data-testid="forwarding-email-input"
                      />
                    </div>

                    {/* Gmail Configuration */}
                    <div className="pt-4 border-t border-white/10">
                      <div className="flex items-center space-x-2 mb-4">
                        <h4 className="text-sm font-medium text-white/70">Gmail SMTP Settings</h4>
                        {emailSettings.gmail_configured ? (
                          <span className="flex items-center text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-sm">
                            <Check className="h-3 w-3 mr-1" />
                            Configured
                          </span>
                        ) : (
                          <span className="flex items-center text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-sm">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not configured
                          </span>
                        )}
                      </div>
                      
                      <p className="text-xs text-white/50 mb-4">
                        To send emails, you need a Gmail account with an{' '}
                        <a 
                          href="https://support.google.com/accounts/answer/185833" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-teal-400 hover:underline"
                        >
                          App Password
                        </a>
                        . Enable 2-Factor Authentication on your Google account first.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                            Gmail Address
                          </Label>
                          <Input
                            type="email"
                            value={emailSettings.gmail_address || ''}
                            onChange={(e) => {
                              handleEmailSettingsChange('gmail_address', e.target.value);
                              if (!isEditingEmail) setIsEditingEmail(true);
                            }}
                            placeholder="yourgmail@gmail.com"
                            className="form-input"
                            data-testid="gmail-address-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                            Gmail App Password
                          </Label>
                          <Input
                            type="password"
                            value={emailSettings.gmail_app_password || ''}
                            onChange={(e) => {
                              handleEmailSettingsChange('gmail_app_password', e.target.value);
                              if (!isEditingEmail) setIsEditingEmail(true);
                            }}
                            placeholder={emailSettings.gmail_configured ? '••••••••••••••••' : 'xxxx xxxx xxxx xxxx'}
                            className="form-input"
                            data-testid="gmail-password-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3 pt-4">
                      <Button
                        onClick={saveEmailSettings}
                        disabled={emailSaving || !isEditingEmail}
                        className="btn-primary"
                        data-testid="save-email-btn"
                      >
                        {emailSaving ? (
                          <div className="spinner mr-2" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        Save Settings
                      </Button>
                      
                      {emailSettings.gmail_configured && emailSettings.forwarding_email && (
                        <Button
                          onClick={testEmailSettings}
                          disabled={testingEmail}
                          variant="outline"
                          className="btn-secondary"
                          data-testid="test-email-btn"
                        >
                          {testingEmail ? (
                            <div className="spinner mr-2" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Send Test Email
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Messages List */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="bg-neutral-900 border border-white/10 rounded-sm p-12 text-center text-white/50">
                      No messages yet.
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`bg-neutral-900 border rounded-sm p-6 ${
                          message.read ? 'border-white/10' : 'border-teal-500/30 bg-teal-500/5'
                        }`}
                        data-testid={`message-${message.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-medium text-white">{message.name}</h3>
                            <a 
                              href={`mailto:${message.email}`}
                              className="text-sm text-teal-400 hover:underline"
                            >
                              {message.email}
                            </a>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className="text-xs text-white/40">
                              {new Date(message.created_at).toLocaleDateString()}
                            </span>
                            {!message.read && (
                              <button
                                onClick={() => markMessageRead(message.id)}
                                className="text-xs text-teal-400 hover:underline"
                                data-testid={`mark-read-${message.id}`}
                              >
                                Mark as read
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-white/70 whitespace-pre-wrap">{message.message}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Site Settings Tab */}
          {activeTab === 'site-settings' && (
            <div data-testid="site-settings-section">
              <div className="mb-8">
                <h2 className="font-heading text-2xl font-semibold text-white">
                  Site Settings
                </h2>
                <p className="text-white/50 text-sm mt-1">
                  Customize your website appearance
                </p>
              </div>

              {/* Hero Image Settings */}
              <div className="bg-neutral-900 border border-white/10 rounded-sm p-6" data-testid="hero-settings">
                <div className="flex items-center space-x-3 mb-4">
                  <Image className="h-5 w-5 text-white/40" />
                  <h3 className="font-medium text-white">Homepage Hero Image</h3>
                </div>
                
                <p className="text-sm text-white/50 mb-6">
                  Upload or change the main banner image displayed on the homepage.
                </p>

                {/* Current Hero Image Preview */}
                {siteSettings.hero_image_url && (
                  <div className="mb-6">
                    <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                      Current Hero Image
                    </Label>
                    <div className="relative w-full max-w-xl aspect-video bg-neutral-800 overflow-hidden rounded-sm border border-white/10">
                      <img
                        src={siteSettings.hero_image_url}
                        alt="Current hero"
                        className="w-full h-full object-cover"
                        data-testid="hero-preview"
                      />
                    </div>
                  </div>
                )}

                {/* Upload Section */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                      Upload New Image
                    </Label>
                    <div className="flex items-center gap-4">
                      <input
                        ref={heroFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleHeroImageUpload}
                        className="hidden"
                        id="hero-upload"
                        data-testid="hero-file-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => heroFileInputRef.current?.click()}
                        disabled={heroUploading}
                        className="btn-secondary"
                        data-testid="hero-upload-btn"
                      >
                        {heroUploading ? (
                          <div className="spinner mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        {heroUploading ? 'Uploading...' : 'Choose Image'}
                      </Button>
                      <span className="text-xs text-white/40">
                        JPG, PNG, GIF, WebP (max 10MB)
                      </span>
                    </div>
                  </div>

                  {/* Or use URL */}
                  <div className="pt-4 border-t border-white/10">
                    <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                      Or paste image URL
                    </Label>
                    <Input
                      type="url"
                      value={siteSettings.hero_image_url || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, hero_image_url: e.target.value }))}
                      placeholder="https://example.com/image.jpg"
                      className="max-w-xl form-input"
                      data-testid="hero-url-input"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="pt-4">
                    <Button
                      onClick={saveSiteSettings}
                      disabled={siteSaving}
                      className="btn-primary"
                      data-testid="save-site-btn"
                    >
                      {siteSaving ? (
                        <div className="spinner mr-2" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Save Hero Image
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Account & Security Tab */}
          {activeTab === 'account' && (
            <div data-testid="account-section">
              <div className="mb-8">
                <h2 className="font-heading text-2xl font-semibold text-white">
                  Account & Security
                </h2>
                <p className="text-white/50 text-sm mt-1">
                  Manage your password and user accounts
                </p>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                {/* Change My Password */}
                <div className="bg-neutral-900 border border-white/10 rounded-sm p-6" data-testid="change-password-section">
                  <div className="flex items-center space-x-3 mb-4">
                    <Key className="h-5 w-5 text-white/40" />
                    <h3 className="font-medium text-white">Change My Password</h3>
                  </div>
                  
                  <form onSubmit={handleChangeMyPassword} className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                        Current Password
                      </Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? 'text' : 'password'}
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                          className="form-input pr-10"
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                        >
                          {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                        New Password
                      </Label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? 'text' : 'password'}
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                          className="form-input pr-10"
                          data-testid="new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                        >
                          {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                        Confirm New Password
                      </Label>
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        className="form-input"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={passwordChanging}
                      className="btn-primary w-full"
                      data-testid="change-password-btn"
                    >
                      {passwordChanging ? (
                        <div className="spinner mr-2" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Change Password
                    </Button>
                  </form>
                </div>

                {/* Reset User Password (Admin) */}
                <div className="bg-neutral-900 border border-white/10 rounded-sm p-6" data-testid="reset-user-password-section">
                  <div className="flex items-center space-x-3 mb-4">
                    <Users className="h-5 w-5 text-white/40" />
                    <h3 className="font-medium text-white">Reset User Password</h3>
                  </div>
                  
                  <p className="text-sm text-white/50 mb-4">
                    As an admin, you can reset passwords for other users.
                  </p>
                  
                  <form onSubmit={handleResetUserPassword} className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                        Select User
                      </Label>
                      <select
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                        className="w-full form-input bg-neutral-800 text-white"
                        data-testid="select-user"
                      >
                        <option value="">-- Select a user --</option>
                        {allUsers.map(u => (
                          <option key={u.id} value={u.email}>
                            {u.name} ({u.email})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 block">
                        New Password for User
                      </Label>
                      <Input
                        type="text"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        placeholder="Enter new password"
                        className="form-input"
                        data-testid="reset-password-input"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={resettingPassword || !selectedUser}
                      className="btn-primary w-full"
                      data-testid="reset-user-password-btn"
                    >
                      {resettingPassword ? (
                        <div className="spinner mr-2" />
                      ) : (
                        <Key className="h-4 w-4 mr-2" />
                      )}
                      Reset Password
                    </Button>
                  </form>
                  
                  {/* User List */}
                  <div className="mt-6 pt-4 border-t border-white/10">
                    <p className="text-xs uppercase tracking-wider text-white/60 mb-3">
                      Registered Users
                    </p>
                    <div className="space-y-2">
                      {allUsers.map(u => (
                        <div key={u.id} className="flex items-center justify-between text-sm p-2 bg-neutral-800/50 rounded">
                          <span className="text-white">{u.name}</span>
                          <span className="text-white/50">{u.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Artwork Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl bg-neutral-900 border-white/10 max-h-[90vh] overflow-y-auto" data-testid="artwork-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">
              {editingArtwork ? 'Edit Artwork' : 'Add New Artwork'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title" className="text-xs uppercase tracking-wider text-white/60">
                Title *
              </Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="mt-1 form-input"
                data-testid="modal-title-input"
              />
            </div>

            {/* Image Upload Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-white/60">
                  Images * <span className="text-white/40">({artworkImages.length}/10)</span>
                </Label>
                {artworkImages.length > 1 && (
                  <span className="text-xs text-white/40">Click image to set as primary</span>
                )}
              </div>
              
              {/* Uploaded Images Preview */}
              {artworkImages.length > 0 && (
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {artworkImages.map((url, index) => (
                    <div 
                      key={index} 
                      className={`relative group aspect-square bg-neutral-800 rounded-sm overflow-hidden border-2 transition-all cursor-pointer ${
                        index === 0 
                          ? 'border-teal-500 ring-2 ring-teal-500/30' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      onClick={() => setPrimaryImage(index)}
                      title={index === 0 ? 'Primary image (thumbnail)' : 'Click to set as primary'}
                    >
                      <img
                        src={url}
                        alt={`Artwork ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {index === 0 && (
                        <span className="absolute top-1 left-1 bg-teal-500 text-black text-[10px] px-1.5 py-0.5 rounded-sm font-medium">
                          Primary
                        </span>
                      )}
                      {index !== 0 && (
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <span className="text-white text-[10px] font-medium">Set Primary</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeArtworkImage(index);
                        }}
                        className="absolute top-1 right-1 z-10 bg-red-500/80 hover:bg-red-500 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${index}`}
                        title="Remove image"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <div className="flex items-center gap-4">
                <input
                  ref={artworkFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleArtworkImageUpload}
                  className="hidden"
                  id="artwork-upload"
                  data-testid="artwork-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => artworkFileInputRef.current?.click()}
                  disabled={artworkUploading || artworkImages.length >= 10}
                  className="btn-secondary"
                  data-testid="artwork-upload-btn"
                >
                  {artworkUploading ? (
                    <div className="spinner mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {artworkUploading ? 'Uploading...' : artworkImages.length >= 10 ? 'Max 10 Images' : 'Upload Images'}
                </Button>
                <span className="text-xs text-white/40">
                  {artworkImages.length >= 10 
                    ? 'Maximum images reached' 
                    : `Up to ${10 - artworkImages.length} more images`}
                </span>
              </div>
              
              {artworkImages.length === 0 && (
                <p className="text-amber-400/80 text-xs mt-2 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Please upload at least one image
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dimensions" className="text-xs uppercase tracking-wider text-white/60">
                  Dimensions *
                </Label>
                <Input
                  id="dimensions"
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={handleChange}
                  required
                  className="mt-1 form-input"
                  placeholder="24 x 36 inches"
                  data-testid="modal-dimensions-input"
                />
              </div>
              <div>
                <Label htmlFor="year" className="text-xs uppercase tracking-wider text-white/60">
                  Year
                </Label>
                <Input
                  id="year"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="mt-1 form-input"
                  placeholder="2023"
                  data-testid="modal-year-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category" className="text-xs uppercase tracking-wider text-white/60">
                  Category
                </Label>
                <Input
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="mt-1 form-input"
                  placeholder="Public Art"
                  data-testid="modal-category-input"
                />
              </div>
              <div>
                <Label htmlFor="status" className="text-xs uppercase tracking-wider text-white/60">
                  Status
                </Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="mt-1 w-full px-4 py-2 bg-white/5 border border-white/20 rounded-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  data-testid="modal-status-input"
                >
                  <option value="available" className="bg-neutral-900">Available</option>
                  <option value="sold" className="bg-neutral-900">Sold (Past Work)</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="text-xs uppercase tracking-wider text-white/60">
                Description
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="mt-1 form-input"
                rows={3}
                data-testid="modal-description-input"
              />
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary"
                data-testid="modal-cancel-btn"
              >
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="modal-save-btn">
                {editingArtwork ? 'Save Changes' : 'Add Artwork'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md bg-neutral-900 border-white/10" data-testid="delete-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-400">
              Delete Artwork
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/70 mt-2">
            Are you sure you want to delete "{deletingArtwork?.title}"? This action cannot be undone.
          </p>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              className="btn-secondary"
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
